import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Stack, Typography } from '@mui/material'
import { Button } from '@repo/ui'
import { AlertCircle, Mail } from 'lucide-react'

import { ScanTag } from 'components/pages/certificates/productPage/ScanTag'
import type { ScanTagResult } from 'components/pages/certificates/productPage/ScanTag'
import {
  PendingSaleResolveError,
  useResolvePendingSale,
} from 'hooks/api/pendingOwnership'
import type { PendingSale } from 'types'

import { CheckoutEmailRecovery } from './CheckoutEmailRecovery'
import {
  contactBrandLabel,
  getPendingSaleFailureCopy,
  type PendingSaleFailureCopy,
} from './pendingSaleCopy'

export interface PendingSaleClaimDrawerProps {
  sale: PendingSale | null
  open: boolean
  onClose: () => void
}

type Phase = 'scanning' | 'resolving' | 'claimed' | 'failed' | 'recovery'

/**
 * VRG-455 — "Scan to claim ownership", end to end, in ONE sheet.
 *
 * ## To the buyer this is the ordinary claim flow
 *
 * They see an item they bought, they tap "Scan to claim ownership", they scan,
 * it is theirs. Everything that makes this different — that no certificate was
 * bound until the scan, that the record names a product rather than an item,
 * that resolution is keyed on their identity — is a BACKEND fact with no
 * user-facing expression. Nothing here introduces a concept, a word or a screen
 * that the existing incoming-transfer claim does not already have.
 *
 * ## One sheet, several steps (§52)
 *
 * Scan, then working, then the outcome, then (on the one failure that has a way
 * forward) the recovery — all of it inside `ScanTag`, which already owns a
 * step machine and a caller-owned terminal screen. A second drawer for the
 * outcome and a third for the recovery is exactly the stack §52 forbids, and it
 * is the mistake this component is shaped to avoid: level two is a step
 * machine, not a launchpad.
 *
 * ## The failure branch is many branches, not one
 *
 * `pendingSaleCopy` holds the mapping. A buyer never reads an error code and
 * never reads "something went wrong": some outcomes have a path forward and
 * others do not, and a shrug leaves them unable to tell which they are in.
 * Worse than a shrug is the wrong one — "we couldn't find your purchase, try
 * another email" was being shown to buyers who already owned the item and to
 * buyers whose sale had been refunded, until the API told the three apart.
 */
export const PendingSaleClaimDrawer = ({
  sale,
  open,
  onClose,
}: PendingSaleClaimDrawerProps) => {
  const navigate = useNavigate()
  const resolve = useResolvePendingSale()

  const [phase, setPhase] = useState<Phase>('scanning')
  const [failure, setFailure] = useState<PendingSaleFailureCopy | null>(null)
  const [claimedCertificateId, setClaimedCertificateId] = useState<string | null>(null)
  const [recoveryStep, setRecoveryStep] = useState<'email' | 'code'>('email')
  /**
   * WHAT THE LAST SCAN IDENTIFIED, held so the recovery path can retry without
   * asking the buyer to tap the item a second time.
   *
   * They are already holding it, they already scanned it, and the thing that
   * changed is on our side (an address is now verified). Making them re-scan
   * would present our bookkeeping as their chore.
   */
  const [lastScan, setLastScan] = useState<ScanTagResult | null>(null)
  /**
   * REMOUNT KEY for `ScanTag`, bumped by "Try again".
   *
   * `ScanTag` owns its own scan status and only resets it when `open` flips —
   * so after a refusal its status is still `success`, and simply putting this
   * component back into its `scanning` phase would leave the sheet showing
   * ScanTag's generic "Tag scanned" fallback and, two seconds later, closing
   * itself. Remounting is what actually re-arms the scan.
   */
  const [attempt, setAttempt] = useState(0)

  const brandName = sale?.company?.name ?? null
  const supportEmail = sale?.company?.support_email ?? null

  // Every open starts clean. A sheet that reopens on the previous attempt's
  // failure would show a stale verdict about a scan that has not happened yet.
  useEffect(() => {
    if (!open) return
    setPhase('scanning')
    setFailure(null)
    setClaimedCertificateId(null)
    setRecoveryStep('email')
    setLastScan(null)
    setAttempt(0)
  }, [open])

  const runResolve = (scan: ScanTagResult) => {
    setPhase('resolving')
    resolve.mutate(scan, {
      onSuccess: data => {
        setClaimedCertificateId(data.certificate_id)
        setPhase('claimed')
      },
      onError: error => {
        const code = error instanceof PendingSaleResolveError ? error.code : null
        setFailure(getPendingSaleFailureCopy(code, brandName))
        setPhase('failed')
      },
    })
  }

  const handleScanSuccess = (result?: ScanTagResult) => {
    /*
     * NO IDENTIFIER MEANS NO CLAIM, and it is reported as such.
     *
     * A scan that completes without telling us which tag was read cannot
     * resolve anything: the whole design turns on the scan being the resolver,
     * because nobody guessed which unit shipped to this buyer. Silently
     * treating it as a success would be the one lie this feature cannot afford.
     *
     * Reachable today via the dev-only "Simulate scan" control, which produces
     * no tag by construction. On a real tap the `/mobile/scan` round-trip
     * carries the certificate id.
     */
    if (!result?.certificateId && !result?.tagUid) {
      setFailure(getPendingSaleFailureCopy(null, brandName))
      setPhase('failed')
      return
    }

    setLastScan(result)
    runResolve(result)
  }

  const contactHref = supportEmail
    ? `mailto:${supportEmail}?subject=${encodeURIComponent(
        `About my purchase${sale?.product?.title ? ` — ${sale.product.title}` : ''}`,
      )}`
    : null

  /**
   * The failure's secondary choices, rendered under the primary one.
   *
   * `contact-brand` degrades to a plain instruction when the brand has set no
   * `support_email`: a button that opens an empty mail client is worse than a
   * sentence, because it looks like it did something.
   */
  const failureExtraActions = failure ? (
    <>
      {failure.actions.includes('contact-brand') &&
        (contactHref ? (
          <Button
            variant='secondary'
            sx={{ width: '100%' }}
            onClick={() => {
              window.location.href = contactHref
            }}
          >
            {contactBrandLabel(brandName)}
          </Button>
        ) : (
          <Typography
            sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5, textAlign: 'center' }}
          >
            Get in touch with {brandName || 'the brand'} using the contact details on your
            order.
          </Typography>
        ))}
    </>
  ) : null

  /*
   * WHAT THE SHEET IS SHOWING. `ScanTag`'s caller-owned terminal slot is used
   * for every post-scan step — see its `successTone`, which is why a refusal
   * does not land under a green check.
   */
  const terminal = (() => {
    switch (phase) {
      case 'resolving':
        return {
          title: 'Claiming ownership…',
          description: 'Matching what you scanned to your purchase.',
          tone: 'success' as const,
          content: null as React.ReactNode,
          actionLabel: undefined as string | undefined,
          onAction: undefined as (() => void) | undefined,
          icon: undefined as React.ReactNode,
          extra: null as React.ReactNode,
        }

      case 'claimed':
        return {
          title: "It's yours",
          description: sale?.product?.title
            ? `${sale.product.title} is now registered to your account.`
            : 'This item is now registered to your account.',
          tone: 'success' as const,
          content: null as React.ReactNode,
          actionLabel: 'View certificate',
          onAction: () => {
            onClose()
            if (claimedCertificateId) navigate(`/certificates/${claimedCertificateId}`)
          },
          icon: undefined as React.ReactNode,
          extra: null as React.ReactNode,
        }

      case 'recovery':
        return {
          title:
            recoveryStep === 'code' ? 'Enter your code' : 'Which email did you order with?',
          description: undefined,
          tone: 'success' as const,
          content: (
            <CheckoutEmailRecovery
              onStepChange={setRecoveryStep}
              onLinked={() => {
                /*
                 * Straight back into the resolve the buyer originally asked
                 * for. The address is now verified, which is the only thing
                 * that was missing — returning them to a confirmation screen
                 * would make them start the claim over for no reason.
                 */
                if (lastScan) {
                  runResolve(lastScan)
                  return
                }
                setPhase('scanning')
              }}
            />
          ),
          actionLabel: undefined as string | undefined,
          onAction: undefined as (() => void) | undefined,
          icon: <Mail size={40} />,
          extra: null as React.ReactNode,
        }

      case 'failed':
        return {
          title: failure?.title ?? '',
          description: failure?.body,
          tone: 'failure' as const,
          content: null as React.ReactNode,
          // The PRIMARY action is the recovery when there is one, because it is
          // the only thing on this screen that can still get the buyer their
          // item. Otherwise it is "try again", and where neither applies the
          // screen has only the sheet's own dismissal, which is correct — an
          // invented action would promise something we cannot do.
          actionLabel: failure?.actions.includes('enter-checkout-email')
            ? 'Enter checkout email'
            : failure?.actions.includes('try-again')
              ? 'Try again'
              : undefined,
          onAction: failure?.actions.includes('enter-checkout-email')
            ? () => {
                setRecoveryStep('email')
                setPhase('recovery')
              }
            : failure?.actions.includes('try-again')
              ? () => {
                  setFailure(null)
                  setPhase('scanning')
                  // Remount ScanTag — see `attempt`.
                  setAttempt(value => value + 1)
                }
              : undefined,
          icon: <AlertCircle size={40} />,
          extra: failureExtraActions,
        }

      default:
        return null
    }
  })()

  return (
    <ScanTag
      key={attempt}
      open={open}
      onClose={onClose}
      onScanSuccess={handleScanSuccess}
      title='Claim ownership'
      pageTitle='Scan the tag'
      successTitle={terminal?.title}
      successDescription={terminal?.description}
      // No wrapper — ScanTag already insets and left-aligns this slot.
      successContent={terminal?.content ?? undefined}
      successActionLabel={terminal?.actionLabel}
      successActionOnClick={terminal?.onAction}
      successIcon={terminal?.icon}
      successTone={terminal?.tone}
      successExtraActions={
        terminal?.extra ? <Stack spacing={1.5}>{terminal.extra}</Stack> : undefined
      }
    />
  )
}

export default PendingSaleClaimDrawer
